import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@headlessui/react';
import { CaretRight } from '@phosphor-icons/react';

export const AButton = ({
  href,
  target,
  rel,
  onClick,
  htmlType,
  children,
  type = 'primary', // default
  size = 'md', // default
  fullWidth,
  hideIcon = true, // hide on default
  disabled,
}) => {
  const isLink = !!href; // infer from presence of href
  const iconColor = useMemo(() => {
    switch (type) {
      case 'primary':
        return 'var(--white)';
      case 'secondary':
        return 'var(--white)';
      case 'tertiary':
        return 'var(--black)';
      default:
        return 'var(--white)';
    }
  }, [type]);

  return (
    <Button
      as={isLink ? 'a' : 'button'}
      {...(isLink ? { href, target, rel } : { htmlType })}
      onClick={onClick}
      className={classNames(
        'abutton flex items-center justify-center gap-2 rounded-full border text-sm font-medium transition-smooth',
        {
          'border-black/10 bg-azure-800 !text-white hover:bg-azure-900':
            type === 'primary', // azure-colored button
          'border-white/10 bg-black/10 !text-black hover:bg-black/20':
            type === 'secondary', // black-colored button
          'border-black/10 bg-white !text-black hover:bg-gray':
            type === 'tertiary', // white-colored button
          'border-transparent bg-transparent !text-white hover:border-white/10':
            type === 'quaternary', // transparent button
          'h-14 px-6 py-4': size === 'lg',
          'h-12 px-6 py-4': size === 'md',
          'h-8 !gap-1 px-3 py-2': size === 'sm',
          'w-full': fullWidth,
          'pointer-events-none cursor-not-allowed opacity-50': disabled,
          '[&_svg]:hidden': hideIcon,
        }
      )}
    >
      {children}
      <CaretRight size={16} weight="bold" color={iconColor} />
    </Button>
  );
};

AButton.propTypes = {
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  htmlType: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary']),
  size: PropTypes.oneOf(['lg', 'md', 'sm']),
  disabled: PropTypes.bool,
  hideIcon: PropTypes.bool,
  fullWidth: PropTypes.bool,
};
